import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Input, Empty, Table, Td } from "../components/UI";
import { CocoaSelect } from "../components/CocoaSelect";
import { FileSignature, Search, ShieldCheck, ShieldAlert, Copy, Lock } from "lucide-react";
import { toast } from "sonner";

const RESOURCE_LABEL = { progress_note: "Progress Note", treatment_plan: "Treatment Plan", consent_form: "Consent Form", clinical_form: "Clinical Form", entrance_survey: "Entrance Survey" };
const RESOURCE_LINK = (r, id) => ({ progress_note: `/clinical/progress-notes/${id}`, treatment_plan: `/clinical/treatment-plans/${id}`, consent_form: "/clinical/consents", clinical_form: "/clinical-forms" }[r] || null);

export default function SignatureAudit() {
  useEffect(() => { document.title = "Signature Audit | COCOA EHR"; }, []);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [rows, setRows] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [resource, setResource] = useState("");
  const [signerType, setSignerType] = useState("");
  const [verify, setVerify] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/signatures", { params: { search: search || undefined, resource: resource || undefined, signer_type: signerType || undefined } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [isAdmin, search, resource, signerType]);

  if (!isAdmin) {
    return (
      <Card className="p-10 text-center" data-testid="signature-audit-denied">
        <Lock size={28} className="mx-auto mb-2 text-[#7B6157]" />
        <div className="font-heading text-lg font-bold text-[#3E2723]">Admin access required</div>
        <div className="text-sm text-[#7B6157]">The signature audit registry is restricted to administrators for compliance review.</div>
      </Card>
    );
  }

  const doVerify = async (sid) => {
    try {
      const r = await api.get(`/signatures/${sid}/verify`);
      setVerify((v) => ({ ...v, [sid]: r.data.integrity }));
      toast.success(r.data.integrity === "verified" ? "Integrity verified: document unaltered" : `Integrity check: ${r.data.integrity}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const copyHash = (h) => { navigator.clipboard.writeText(h); toast.success("SHA-256 hash copied"); };

  const staffCount = rows.filter((r) => r.signer_type !== "client").length;

  return (
    <div data-testid="signature-audit-page">
      <PageHeader title="Signature Audit" subtitle="Every e-signature captured across the clinic, with SIG-ID, SHA-256 integrity hash, and one-click tamper verification per HIPAA 164.312(c)(1)" />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
          <Input data-testid="sig-search" className="pl-9" placeholder="Search signer or SIG-ID…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <CocoaSelect testId="sig-resource-filter" className="w-44" placeholder="All documents" value={resource} onChange={setResource}
          options={Object.entries(RESOURCE_LABEL).map(([v, l]) => [v, l])} />
        <CocoaSelect testId="sig-signer-filter" className="w-36" placeholder="All signers" value={signerType} onChange={setSignerType}
          options={[["staff", "Staff"], ["client", "Client"]]} />
        <div className="ml-auto flex gap-2 text-[11px] font-semibold text-[#7B6157]">
          <span className="rounded-full bg-[#F5F2EB] px-2.5 py-1" data-testid="sig-total">{rows.length} signatures</span>
          <span className="rounded-full bg-[#F5F2EB] px-2.5 py-1">{staffCount} staff · {rows.length - staffCount} client</span>
        </div>
      </div>

      <Card className="overflow-x-auto">
        {rows.length === 0 ? <Empty text="No signatures match these filters" /> : (
          <Table headers={["SIG-ID", "Signer", "Document", "Client", "Signed At (UTC)", "IP", "SHA-256 Hash", "Integrity"]}>
            {rows.map((r) => {
              const link = RESOURCE_LINK(r.resource, r.resource_id);
              const v = verify[r.signature_id];
              return (
                <tr key={r.signature_id} className="border-b border-[#EFEBE0] last:border-0" data-testid={`sig-row-${r.signature_id}`}>
                  <Td><span className="font-mono text-xs font-semibold text-[#4E342E]">{r.signature_id}</span></Td>
                  <Td>
                    <div className="text-sm font-semibold text-[#3E2723]">{r.signer_name || "-"}</div>
                    <span className={`rounded-full px-1.5 py-px text-[9px] font-bold uppercase ${r.signer_type === "client" ? "bg-[#EDE7F6] text-[#5E35B1]" : "bg-[#E8F5E9] text-[#2E7D32]"}`}>{r.signer_type || "staff"}</span>
                  </Td>
                  <Td>{link
                    ? <Link to={link} className="text-sm font-semibold text-[#4E342E] hover:underline">{RESOURCE_LABEL[r.resource] || r.resource}</Link>
                    : <span className="text-sm">{RESOURCE_LABEL[r.resource] || r.resource}</span>}</Td>
                  <Td>{r.client_name || "-"}</Td>
                  <Td><span className="text-xs tabular-nums">{(r.signed_at || "").replace("T", " ").slice(0, 16)}</span></Td>
                  <Td><span className="font-mono text-[10px]">{r.ip || "-"}</span></Td>
                  <Td>
                    <button onClick={() => copyHash(r.doc_hash)} title={r.doc_hash} data-testid={`sig-hash-${r.signature_id}`}
                      className="flex items-center gap-1 font-mono text-[10px] text-[#6D4C41] hover:text-[#3E2723]">
                      {(r.doc_hash || "").slice(0, 16)}… <Copy size={10} />
                    </button>
                  </Td>
                  <Td>
                    {v ? (
                      v === "verified"
                        ? <span data-testid={`sig-verified-${r.signature_id}`} className="flex items-center gap-1 rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold text-[#2E7D32]"><ShieldCheck size={11} /> Verified</span>
                        : <span className="flex items-center gap-1 rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]"><ShieldAlert size={11} /> {v === "unknown" ? "Unknown" : "Mismatch"}</span>
                    ) : (
                      <Btn variant="outline" data-testid={`sig-verify-${r.signature_id}`} className="px-2.5 py-1 text-[11px]" onClick={() => doVerify(r.signature_id)}>Verify</Btn>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>
    </div>
  );
}
